import PersonalizeUtil from 'personalize_util';
import ErrorTracker from 'error_tracker';
import { default as devLog, isDev } from 'core/utils/devLog';
import getLTV from './getLTV';
import { findCartPrimaryListing, getShareasalePixelUrl } from 'cart/utils';
import { completeTransaction } from 'core/utils/Braze';
import TrackUtil from 'core/utils/TrackUtil';
import fireAdwordsConversions from './fireAdwordsConversions';
import fireGtagPurchase from './fireGtagPurchase';
import ApiRequest from 'core/api/request';

/**
 * Trigger all checkout confirmation conversions
 *
 * @param {Object} mpOrder
 * @param {Object} confBlob - { cartCheckout, multicartCheckout, guestCheckout, ...metadata }
 */
export default function conversions(mpOrder, confBlob) {
	const promises = mpOrder.orders.map((order) => {
		const cart = mpOrder.carts.find(c => c.storeId === order.storeId);
		return orderConversions(order, cart, confBlob);
	});
	return Promise.allSettled(promises);
}

/**
 * Trigger all checkout confirmation conversions for a given order + cart
 *
 * @param {Object} order
 * @param {Object} cart
 * @param {Object} confBlob - { cartCheckout, guestCheckout }
 */
function orderConversions(order, cart, confBlob) {
	const listing = findCartPrimaryListing(cart) || {};
	const { listingId, slug } = listing;
	const {
		cartCheckout,
		guestCheckout,
		multicartCheckout,
		reactCheckout,
		billingAddressSameAsShipping,
	} = (confBlob || {});
	// False if listingId was not found in impressions list:
	const isRecommendation = PersonalizeUtil.isRecommendedListing(listingId);
	devLog(`Triggering XO Conversions for ${order?.id}`, order, cart, listing, confBlob, 'rec?', isRecommendation);

	PersonalizeUtil.sendEvent("Purchase", listingId);

	fireFbPurchase({
		order: order,
		listingIds: [listingId],
		customerId: order?.customerId,
	});

	fireShareasale(order, cart);
	if (isRecommendation) {
		fireRecommendationConv(order.totalWithServiceFee, listingId);
	}
	fireMNTNPixel(order);

	// Return all the promisified code as a single promise, so we can wait for all of it to finish
	// So we don't miss any conversion attribution
	return Promise.allSettled([
		fireBraze(order, cart),
		fireJustuno(order, cart),
		fireBing({
			total: order.totalWithServiceFee,
			currencyCode: order.currencyCode,
		}),
		fireTriage(order, cart),
		fireAdwordsConversions(order),
		fireGtagPurchase(order, cart),
		fireMpCheckoutFlow(order.id, slug, order?.couponCode),
	]);
}


const fireBing = ({ total, currencyCode }) => {
	return TrackUtil.trackEvent("thankyou", {
		bing: [{
			event_category: "purchase",
			event_label: "page",
			revenue_value: total / 100,
			currency: currencyCode,
		}],
	});
};

/**
 * @param {Object} order
 * @param {Object} cart
 */
const fireBraze = (order, cart) => {
	return completeTransaction(order, cart);
};

const fireFbPurchase = ({ order, listingIds, customerId }) => {
	const { id: orderId, currencyCode, subscriptions } = order;
	const total = order.totalWithServiceFee || order.total;
	const [ltv, termMultiplier] = getLTV({ subscriptions, total });

	const data = {
		external_id: customerId,
		currency: currencyCode,
		value: ltv / 100,
		content_type: 'product',
		content_ids: listingIds,
	};
	const eventId = { eventID: orderId };
	devLog({ eventName: `Purchase (fbq)`, args: { data, eventId } });
	if (!window?.fbq) return;
	window.fbq('track', 'Purchase', data, eventId);
};

const fireShareasale = (order, cart) => {
	if (!cart || !cart?.id || !order?.id) return null;

	const shareasaleUrl = getShareasalePixelUrl(order, cart);
	if (!shareasaleUrl) return null;

	if (window.delayLoadScript) {
		window.delayLoadScript("https://shareasale-analytics.com/j.js", 'shareasale-analytics');
	}

	const sasImg = new Image(1, 1);
	sasImg.src = shareasaleUrl;
};

const fireJustuno = (order, cart) => {
	const formattedProducts = cart.products.map((cartProduct) => ({
		// keys here match Justuno's docs: https://support.justuno.com/tracking-visitor-carts-conversions-past-orders
		productid: cartProduct.productInstance.product.id,
		name: cartProduct.productInstance.product.name,
		quantity: cartProduct.quantity,
		price: parseInt(cartProduct.price, 10) / 100,
	}));
	const data = {
		orderId: order.id, // Justuno requires an order id and TrackUtil depends on this key to send it
		total: order.totalWithServiceFee / 100,
		subtotal: order.subTotal / 100,
		tax: order.totalTax / 100,
		shipping: order.totalShipping / 100,
		currency: order.currencyCode,
		orderItems: formattedProducts,
	};
	return TrackUtil.trackEvent("order", { justuno: [data] });
};

const fireMpCheckoutFlow = (orderId, slug, coupon) => {
	const p1 = TrackUtil.trackEvent("flow_mp_checkout", {
		braze: [{
			step_name: "confirmation",
			selector: slug,
		}],
		statjoy: [{
			step_name: "confirmation",
			selector: slug,
			cus1: orderId,
			cus2: coupon,
		}],
		fullstory: [{
			slug_str: slug,
			step_str: "confirmation",
		}],
	});

	const p2 = TrackUtil.trackEvent("step", {
		gtag: [{
			event_category: "mp_checkout",
			event_label: "confirmation",
		}],
	});

	return Promise.allSettled([p1, p2]);
};

/**
 * Fired only for recommendation conversions
 * Should NOT be called when purchase NOT from recent rec impression*
 * rec impression determined by `isRecommendedListing`
 *
 * @param {Number} total - total value (typically order total)
 * @param {Number} listingId
 */
const fireRecommendationConv = (total, listingId) => {
	const value = Math.round(total / 100);

	TrackUtil.trackEvent("recommendation_conversion", {
		gtag: [{
			event_category: 'recommendations',
			value: value,
		}],
		statjoy: [{
			cus1: value,
			cus2: listingId,
		}],
	});

	PersonalizeUtil.onPurchase(listingId, value);
};


/**
 * Mounts a script tag to mount a tracking pixel for MNTN
 * @param {Object} order
 */
const fireMNTNPixel = (order) => {
	devLog({ eventName: `MNTN Pixel`, args: { order } });

	if (isDev()) return;

	let x = null, p, q, m,
		o="33590",
		l=order.id,
		i=order.totalWithServiceFee / 100,
		c="",
		k="",
		g="",
		j="",
		u="",
		shadditional="";
	try {
		p = top.document.referer !== "" ?
			encodeURIComponent(top.document.referrer.substring(0,512)) :
			"";
	} catch (n) {
		p = document.referrer!==null ?
			document.referrer.toString().substring(0,512) :
			"";
	}
	try {
		q = window && window.top && document.location && window.top.location===document.location ?
			document.location :
			window && window.top && window.top.location && "" !== window.top.location ?
				window.top.location :
				document.location;
	} catch (b) {
		q=document.location;
	}
	try {
		m = parent.location.href !== "" ?
			encodeURIComponent(parent.location.href.toString().substring(0,512)) :
			"";
	} catch (z) {
		try {
			m = q !== null ?
				encodeURIComponent(q.toString().substring(0,512)) :
				"";
		} catch (h) {
			m = "";
		}
	}
	let A, y = document.createElement("script"), w = null, v = document.getElementsByTagName("script");
	let t = Number(v.length)-1, r = document.getElementsByTagName("script")[t];
	if (typeof A==="undefined") {
		A = Math.floor(Math.random()*100000000000000000);
	}
	w = "dx.mountain.com/spx?conv=1&shaid="+o+"&tdr="+p+"&plh="+m+"&cb="+A+"&shoid="+l+"&shoamt="+i+"&shocur="+c+"&shopid="+k+"&shoq="+g+"&shoup="+j+"&shpil="+u+shadditional;
	y.type="text/javascript";
	y.src = ("https:" === document.location.protocol ?
		"https://" :
		"http://"
	) + w;
	r.parentNode.insertBefore(y,r);
};

/**
 * Trigger some bug triage stats...
 * Specificially when missing required product surveys
 */
const fireTriage = async (order, cart) => {
	// Are there subs with required survey fields?
	const purchasedProductIds = (cart.products || []).map(cp => cp?.productInstance?.product?.id).filter(pid => !!pid);
	const { subscriptions=[] } = order;
	subscriptions.forEach(sub => {
		ApiRequest.get(`/products/for_sub/${sub.id}`).then((products=[]) => {
			// Filter to only the products that were purchased
			// Since `for_sub` API may return more products than what was purchased
			const purchasedProducts = products.filter(p => purchasedProductIds.includes(p.id));
			const surveys = purchasedProducts.map(p => p.surveys).flat();
			const reqFieldIds = surveys.map(s => {
				let ids = [];
				if (s?.fields?.length > 0) {
					let reqFields = s.fields.filter(f => f.required);
					let reqIds = reqFields.map(f => f.id);
					if (reqIds?.length > 0) {
						ids = ids.concat(reqIds);
					}
				}
				return ids;
			}).flat();

			const hasRequired = reqFieldIds?.length > 0;
			if (hasRequired) {
				// Hit `/subscriptions/3140315` to check sub.surveys[x].answers[y].value
				// where sub.surveys[x].answers[y].field_id is in requiredIds
				TrackUtil.trackEvent("shouldHaveSurveyAnswers", {
					fullstory: [{
						subId_str: `${sub.id}`,
					}],
					statjoy: [{
						cus1: `${sub.id}`,
					}],
				});

				ApiRequest.get(`/subscriptions/${sub.id}`).then((sub) => {
					if (!(sub?.surveys?.length > 0)) {
						let idStr = reqFieldIds.join(",");
						TrackUtil.trackEvent("missingSurveyAnswers", {
							fullstory: [{
								step_str: "none",
								fieldIds_str: idStr,
							}],
							statjoy: [{
								step_name: "none",
								cus1: idStr,
							}],
						});
						return;
					}

					let missingFields = [];
					for (let i = 0; i < reqFieldIds.length; i++) {
						let fieldId = reqFieldIds[i];
						let survey = sub.surveys.find(s => s?.answers?.find(a => a.fieldId === fieldId));
						if (!survey) {
							missingFields.push(fieldId);
						}
					}

					if (missingFields.length > 0) {
						const notes = missingFields.join(",");
						TrackUtil.trackEvent("missingSurveyAnswers", {
							fullstory: [{
								step_str: "missing",
								notes_str: notes,
							}],
							statjoy: [{
								step_name: "missing",
								cus1: notes,
							}],
						});
					}
				}).catch(e => ErrorTracker.captureException(e));
			}
		}).catch(e => ErrorTracker.captureException(e));
	});

	return;
};
